import * as React from 'react';
import { cn } from '../lib/cn';
import { UsageBar, usageToneName, type UsageTone } from './UsageBar';

/**
 * ChartMeter — one ratio against a limit, with the threshold drawn on.
 *
 * The right form for "22 of 25 accounts used". NOT a two-slice donut: a donut
 * of two slices is a meter drawn the hard way, it needs a 160px square where
 * this needs a 30px strip, and it asks the reader to judge an angle where this
 * asks them to judge a length.
 *
 * A THIN WRAPPER ON UsageBar, deliberately (DC-8). UsageBar owns the tone
 * ladder and the progressbar semantics; this adds the label row, the threshold
 * tick and the warning. It must never restate the ladder — the whole reason
 * this component exists is that AdminDashboard had grown its own copy that
 * disagreed with UsageBar's at 55% and 65%.
 *
 * Do not reach for this when a big number is GOOD. UsageBar's ladder means
 * usage: past 80% it turns red. A pipeline bar where more is better needs
 * ChartBars, not a meter with an inverted ladder bolted on.
 */

const TEXT_TONE_CLASS: Record<UsageTone, string> = {
  success: 'text-success',
  warning: 'text-warning',
  danger: 'text-danger-dark',
};

export interface ChartMeterProps {
  value: number;
  max: number;
  /** Left of the readout, e.g. "22 of 25 accounts". Say the units. */
  label: React.ReactNode;
  /**
   * Percent at which to draw a tick, e.g. 80 for the capacity cap. Drawing it
   * lets the reader see how far past the line they are; without it they can
   * only be told. Omit when there is no meaningful threshold.
   */
  threshold?: number;
  /**
   * Rendered below the bar once `threshold` is crossed. Always paired with an
   * icon here, so the state never rests on colour alone.
   */
  warning?: React.ReactNode;
  /** Describes the meter in words for a screen reader. Required. */
  ariaLabel: string;
  className?: string;
}

export function ChartMeter({
  value,
  max,
  label,
  threshold,
  warning,
  ariaLabel,
  className,
}: ChartMeterProps) {
  const safeMax = max > 0 ? max : 1;
  const percent = Math.min(100, Math.max(0, (value / safeMax) * 100));
  const tone = usageToneName(percent);
  const crossed = threshold != null && percent >= threshold;

  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex items-baseline justify-between gap-3 text-sm">
        <span className="tabular-nums text-foreground">{label}</span>
        <span className={cn('font-semibold tabular-nums', TEXT_TONE_CLASS[tone])}>
          {Math.round(percent)}%
        </span>
      </div>

      {/* The track is `relative` and NOT clipped, so the tick can overhang it.
          UsageBar does its own clipping internally, so the fill still stops at
          the rounded end. */}
      <div className="relative">
        <UsageBar value={value} max={safeMax} ariaLabel={ariaLabel} className="h-2 bg-card-subtle" />
        {threshold != null && (
          <span
            aria-hidden
            className="absolute -top-0.5 -bottom-0.5 w-0.5 bg-foreground/45"
            style={{ left: `${Math.min(Math.max(threshold, 0), 100)}%` }}
          />
        )}
      </div>

      {threshold != null && (
        <p className="text-right text-[10px] text-muted-foreground">threshold {threshold}%</p>
      )}

      {crossed && warning && (
        // Icon AND words. A red bar on its own is not a message, and colour on
        // its own is not an accessible one.
        <p className="flex items-start gap-1.5 text-xs text-danger-dark">
          <span aria-hidden className="leading-5">
            ⚠
          </span>
          <span>{warning}</span>
        </p>
      )}
    </div>
  );
}
